import { useId, useRef } from "react";
import { bookingOpens, tripStart } from "../../src/days.ts";
import { waitsOnRating } from "../../src/waits.ts";
import type { Item, TripView } from "./api.ts";
import { promptLead, promptWith } from "./ask-ai.ts";
import { ShowOnMap } from "./Details.tsx";
import { kindClass, kindOf, originLabel, personName, price, recommenders } from "./format.ts";
import { litProps, useLastOpened } from "./opened.ts";
import { ItemThumb } from "./Photos.tsx";
import type { Choice } from "./plan.ts";
import { RateMine, WhoElse } from "./Rate.tsx";
import { useCut } from "./Research.tsx";
import { useReadOnly } from "./readOnly.ts";
import { travel } from "./StayInfo.tsx";
import { Chip, Icon, type IconName } from "./souvenir.ts";
import { FoldLine, factLine, Prompt } from "./ui.tsx";

// A choice's options, compared on Bookings (DESIGN.md, Views, Bookings; features/options.md): under a choice
// still to decide, its row in its kind, its options, folded to one line ("6 options"); under a decided choice's
// chosen row, its other options, folded the same way ("6 other options"). Each option is a tile of one height,
// as research's are, opening its card in full (on a computer a modal, TripPage). Picking happens through an AI.

interface Acts {
  trip: TripView;
  // The option's card in full, over Bookings.
  onOpen: (id: string) => void;
  // Show on map: the option on the map, with its choice's others (one with no place yet: its card).
  onShow: (id: string) => void;
  // After I rate one: load the trip again.
  onChanged: () => void;
}

// How many of these wait on my rating (src/waits.ts: options of a choice are rated where they're compared,
// and counted on Bookings' tab and its Waiting for you).
export const toRate = (options: Item[], you: string) => options.filter((o) => waitsOnRating(o, you)).length;

// A choice still to decide, under its row in its kind on Bookings (whose line says how many options it has):
// one line, "Compare the options", folded unless some of them wait on my rating and kept as I leave it on this
// device; opened, their tiles side by side, to compare and rate.
export function ChoiceOptions({
  c,
  folded,
  onFold,
  ...acts
}: Acts & {
  c: Choice;
  folded: boolean;
  onFold: (folded: boolean) => void;
}) {
  const count = c.options.length;
  if (count === 0) return null;
  const id = `options-${c.choice.id}`;
  return (
    <div className="tm-other-options">
      <FoldLine open={!folded} controls={id} onToggle={() => onFold(!folded)}>
        {count === 1 ? "See the option" : "Compare the options"}
      </FoldLine>
      {!folded && (
        <div id={id} className="tm-other-options-body">
          <OptionGrid options={c.options} choice={c.choice} {...acts} />
        </div>
      )}
    </div>
  );
}

// A decided choice's other options, under its chosen row in its kind: one line saying how many, still open and
// not chosen ("6 other options", "2 other options and 1 not chosen"), folded until opened and kept so on this
// device; opened, their tiles, to compare, rate or ask the AI to switch. Called off ones aren't here: they're
// under their kind's cancelled line.
export function OtherOptions({
  c,
  folded,
  onFold,
  ...acts
}: Acts & {
  c: Choice;
  folded: boolean;
  onFold: (folded: boolean) => void;
}) {
  // A read-only trip takes no ratings: none left to give.
  const readOnly = useReadOnly();
  const open = c.options.filter((o) => o.status === "option");
  const out = c.options.filter((o) => o.status === "not_chosen");
  const others = [...open, ...out];
  if (others.length === 0 || !c.picked) return null;
  const left = readOnly ? 0 : toRate(others, acts.trip.you);
  const options = (n: number) => `${n} other ${n === 1 ? "option" : "options"}`;
  const line =
    open.length > 0 && out.length > 0
      ? `${options(open.length)} and ${out.length} not chosen`
      : open.length > 0
        ? options(open.length)
        : `${options(out.length)}, not chosen`;
  const id = `others-${c.choice.id}`;
  return (
    <div className="tm-other-options">
      <FoldLine open={!folded} controls={id} onToggle={() => onFold(!folded)}>
        {line}
        {left > 0 && <span className="tm-sun-text">{` · ${left} to rate`}</span>}
      </FoldLine>
      {!folded && (
        <div id={id} className="tm-other-options-body">
          <OptionGrid options={others} choice={c.choice} {...acts} />
          {/* A read-only trip: no switching, so nothing to ask. */}
          {!readOnly && (
            <div className="tm-other-options-switch">
              <p className="caption tm-muted">To switch, ask your AI:</p>
              <Prompt
                text={promptWith(
                  promptLead(acts.trip.title, c.choice.title),
                  `Let's switch from ${c.picked.title} to [another option].`,
                )}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OptionGrid({ options, choice, ...acts }: Acts & { options: Item[]; choice: Item }) {
  return (
    <div className="tm-option-grid">
      {options.map((o) => (
        <OptionTile key={o.id} option={o} choice={choice} {...acts} />
      ))}
    </div>
  );
}

const STATE: Record<string, { label: string; icon: IconName; tone?: "sun" | "moss" }> = {
  planned: { label: "Chosen", icon: "check", tone: "moss" },
  booked: { label: "Booked", icon: "lock", tone: "sun" },
  done: { label: "Done", icon: "check", tone: "moss" },
  not_chosen: { label: "Not chosen", icon: "close" },
  cancelled: { label: "Cancelled", icon: "close" },
};

// An option, as a tile of one height (DESIGN.md, Bookings; the founder, 2026-10-22: the hotel cards' height
// varied, "implement the UX to be similar to research card"), never as long as what's written about it: its
// photo and name, whether it's chosen, its price and first facts, then the start of its pros and cons, fading
// out where the tile ends; and a foot that's never cut: who else wants it and Show on map, and my rating. The
// rest (every fact, its links, what it's like, notes) is on its card in full, which the whole tile opens, as
// its name's button stretched over it; the foot's buttons sit above that. Lit when it's the last thing opened
// from Bookings.
export function OptionTile({
  option: o,
  choice,
  trip,
  onOpen,
  onShow,
  onChanged,
}: Acts & { option: Item; choice: Item }) {
  const lit = useLastOpened() === o.id;
  const id = useId();
  const main = useRef<HTMLDivElement>(null);
  const cut = useCut(main);
  const state = STATE[o.status];
  const facts = optionFacts(o, choice, trip);
  const friends = recommenders(o);
  const out = o.status === "not_chosen" || o.status === "cancelled";
  const pros = o.pros ?? [];
  const cons = o.cons ?? [];
  return (
    <article
      className={`tm-option-tile${out ? " is-out" : ""}${lit ? ` is-lit ${kindClass(kindOf(o))}` : ""}`}
      aria-labelledby={`${id}-name`}
    >
      <div className="tm-option-tile-head">
        <ItemThumb item={o} trip={trip.id} iconSize={24} square={56} />
        <span className="tm-option-tile-title">
          <h4 className="body-strong tm-option-tile-name">
            <button
              id={`${id}-name`}
              type="button"
              className="tm-option-open"
              {...((o.cost || state) && { "aria-describedby": `${id}-price` })}
              {...litProps(lit)}
              onClick={() => onOpen(o.id)}
            >
              {o.title}
            </button>
          </h4>
          {/* An AI's own idea for this stop or route: one of the options, saying whose. */}
          {o.origin && <span className="caption tm-muted tm-option-tile-origin">{originLabel(o)}</span>}
        </span>
      </div>
      <div className={`tm-option-tile-main${cut ? " is-cut" : ""}`} ref={main}>
        <div className="tm-option-tile-gist">
          {(o.cost || state) && (
            <p id={`${id}-price`} className="tm-option-price">
              {o.cost && <span className="title tm-price-amount">{price(o.cost)}</span>}
              {state && (
                <Chip icon={state.icon} {...(state.tone ? { tone: state.tone } : {})}>
                  {state.label}
                </Chip>
              )}
            </p>
          )}
          {(facts.length > 0 || friends.length > 0) && (
            <ul className="tm-facts">
              {/* A friend's pick, first: it carries their weight where the options are compared (DESIGN.md,
                  recommended_by; From friends leaves options out). */}
              {friends.length > 0 && (
                <li className="callout tm-option-tile-pick">
                  <Icon name="heart" size={18} />
                  <span>{recommendLine(friends)}</span>
                </li>
              )}
              {facts.map((f) => (
                <li key={[f.text].flat().join()} className="callout">
                  <Icon name={f.icon} size={18} />
                  <span>{typeof f.text === "string" ? f.text : factLine(f.text)}</span>
                </li>
              ))}
            </ul>
          )}
          {pros.length + cons.length > 0 && (
            <ul className="tm-proscons" aria-label="Pros and cons">
              {pros.map((p) => (
                <li key={`+${p}`} className="callout is-pro">
                  <Icon name="check" size={18} />
                  <span>{p}</span>
                </li>
              ))}
              {cons.map((c) => (
                <li key={`-${c}`} className="callout is-con">
                  <Icon name="close" size={18} />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="tm-option-tile-foot">
        <div className="tm-option-tile-say">
          <WhoElse item={o} members={trip.members} you={trip.you} />
          {/* Every option opens on the map, picked or not, beside its choice's other options. */}
          <ShowOnMap onClick={() => onShow(o.id)} />
        </div>
        <RateMine item={o} trip={trip.id} you={trip.you} onChanged={onChanged} />
      </div>
    </article>
  );
}

// "Jon recommends", "Jon and Ana recommend", "Jon, Ana and 2 more recommend": the friends whose pick it is, as
// its card names them ("Jon recommends"), on one line of its tile.
function recommendLine(names: string[]) {
  const [first, second] = names;
  if (names.length === 1)
    return (
      <>
        <strong>{first}</strong> recommends
      </>
    );
  if (names.length === 2)
    return (
      <>
        <strong>{first}</strong> and <strong>{second}</strong> recommend
      </>
    );
  return (
    <>
      <strong>{first}</strong>, <strong>{second}</strong> and {names.length - 2} more recommend
    </>
  );
}

const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

// Whether a place's name is only the option's own again: one in the other, whatever the case.
function sameName(place: string, title: string): boolean {
  const [a, b] = [place.trim().toLowerCase(), title.trim().toLowerCase()];
  return a.includes(b) || b.includes(a);
}

// Each with what it says: one fact, or several on one line (factLine).
function optionFacts(o: Item, choice: Item, trip: TripView): { icon: IconName; text: string | string[] }[] {
  const out: { icon: IconName; text: string | string[] }[] = [];
  if (o.duration) out.push({ icon: "clock", text: o.duration });
  if (o.type === "move" && o.from?.name) out.push({ icon: "pin", text: `From ${o.from.name}` });
  // Where it is, unless that's only its name again ("Island Hotel" for "Island Hotel Nagano", "HOTEL WITH" for
  // "HOTEL WITH (Haeundae)": on the founder's trip most hotels' place is named after the hotel).
  if (o.type !== "move" && o.where?.name && !sameName(o.where.name, o.title))
    out.push({ icon: "pin", text: o.where.name });
  // How far the nearest station (or whatever comes first) is: what hotels are compared on.
  const first = o.getting_around?.[0];
  if (first)
    out.push({
      icon: "train",
      text: [first.to, travel(first.how, first.minutes)].filter((p): p is string => Boolean(p)),
    });
  const booker = personName(o.booking?.booker, trip.members, trip.you);
  if (o.status === "booked") {
    const ref = [o.booking?.provider, o.booking?.ref && `ref ${o.booking.ref}`].filter((p): p is string => Boolean(p));
    out.push({ icon: "lock", text: ["Booked", ...ref] });
  } else if (o.booking_needed) {
    const opens = bookingOpens(o.books_from, o.when ?? choice.when, tripStart(trip.dates));
    const today = new Date().toISOString().slice(0, 10);
    out.push({
      icon: "lock",
      text: [
        "Needs booking",
        opens ? (opens > today ? `opens ${DAY.format(new Date(`${opens}T00:00:00Z`))}` : "open now") : null,
        booker ? `${booker} ${booker === "You" ? "book" : "books"}` : null,
      ].filter((p): p is string => Boolean(p)),
    });
  } else if (o.booking?.provider) {
    out.push({ icon: "lock", text: `Book with ${o.booking.provider}` });
  }
  return out;
}
